import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { LuX } from "react-icons/lu";
import SignupAbrLookup from "components/SignupAbrLookup";
import ThemedSelect from "components/FormControls/ThemedSelect";
import validateSignupIdentity from "utils/signupIdentityValidation";
import { createOrganisation } from "utils/organisationAPI";
import { getCityAndState } from "utils/generic";
import "./organisationCreateDialog.css";

const initialValues = {
  companyName: "",
  organizationType: "Individual",
  abnNumber: "",
  acnNumber: "",
  companyStreetAddress: "",
  companyCity: "",
  companyZipCode: "",
  companyCountryId: "",
  companyStateId: "",
};

export default function OrganisationCreateDialog({ open, onClose, onCreated }) {
  const countryData =
    useSelector((store) => store.countryState?.countryStateMenu?.country) || [];
  const stateData =
    useSelector((store) => store.countryState?.countryStateMenu?.state) || [];
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState("");
  useEffect(() => {
    if (open) getCityAndState();
  }, [open]);
  if (!open) return null;
  const update = (name, value) => {
    setValues((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };
  const save = async () => {
    const next = validateSignupIdentity(values);
    if (!values.companyName.trim())
      next.companyName = "Enter your business or legal name.";
    if (!values.companyStreetAddress.trim())
      next.companyStreetAddress = "Enter your street address.";
    if (!values.companyCity.trim()) next.companyCity = "Enter your suburb or city.";
    if (!values.companyCountryId) next.companyCountryId = "Choose your country.";
    if (!values.companyStateId) next.companyStateId = "Choose your state.";
    if (!/^\d{4}$/.test(values.companyZipCode))
      next.companyZipCode = "Enter a valid 4-digit postcode.";
    setErrors(next);
    if (Object.keys(next).length) return;
    setBusy(true);
    setSubmitError("");
    try {
      const organisation = await createOrganisation(values);
      onCreated(organisation);
      setValues(initialValues);
    } catch (error) {
      setSubmitError(error.response?.data?.Result_Message || error.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="organisation-create-backdrop" role="presentation">
      <section
        className="organisation-create-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="organisation-create-title"
      >
        <header>
          <div>
            <h2 id="organisation-create-title">Create organisation</h2>
            <p>Use the same business details required during initial setup.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close create organisation">
            <LuX />
          </button>
        </header>
        <div className="organisation-create-fields">
          {submitError && <div className="organisation-create-error">{submitError}</div>}
          <label>
            Business or legal name <b>*</b>
            <input
              value={values.companyName}
              onChange={(event) => update("companyName", event.target.value)}
            />
            {errors.companyName && <small>{errors.companyName}</small>}
          </label>
          <label>
            Organisation type <b>*</b>
            <ThemedSelect
              value={values.organizationType}
              onChange={(event) => update("organizationType", event.target.value)}
            >
              <option value="Individual">Individual</option>
              <option value="Business">Business</option>
            </ThemedSelect>
          </label>
          <label>
            ABN {values.organizationType === "Business" && <b>*</b>}
            <input
              inputMode="numeric"
              value={values.abnNumber}
              onChange={(event) => update("abnNumber", event.target.value)}
            />
            {errors.abnNumber && <small>{errors.abnNumber}</small>}
          </label>
          {values.organizationType === "Business" && (
            <label>
              ACN <b>*</b>
              <input
                inputMode="numeric"
                value={values.acnNumber}
                onChange={(event) => update("acnNumber", event.target.value)}
              />
              {errors.acnNumber && <small>{errors.acnNumber}</small>}
            </label>
          )}
          <div className="organisation-create-abr">
            <SignupAbrLookup
              companyName={values.companyName}
              abn={values.abnNumber}
              organizationType={values.organizationType}
              onApply={(business) =>
                setValues((current) => ({
                  ...current,
                  companyName: business.companyName,
                  abnNumber: business.abn,
                  ...(business.acn ? { acnNumber: business.acn } : {}),
                }))
              }
            />
          </div>
          <label>
            Street address <b>*</b>
            <input
              value={values.companyStreetAddress}
              onChange={(event) => update("companyStreetAddress", event.target.value)}
            />
            {errors.companyStreetAddress && <small>{errors.companyStreetAddress}</small>}
          </label>
          <label>
            Suburb or city <b>*</b>
            <input
              value={values.companyCity}
              onChange={(event) => update("companyCity", event.target.value)}
            />
            {errors.companyCity && <small>{errors.companyCity}</small>}
          </label>
          <label>
            Country <b>*</b>
            <ThemedSelect
              value={values.companyCountryId}
              onChange={(event) => {
                update("companyCountryId", event.target.value);
                update("companyStateId", "");
              }}
            >
              <option value="">Select country</option>
              {countryData.map((country) => (
                <option key={country._id} value={country._id}>
                  {country.countryName || country.name}
                </option>
              ))}
            </ThemedSelect>
            {errors.companyCountryId && <small>{errors.companyCountryId}</small>}
          </label>
          <label>
            State or territory <b>*</b>
            <ThemedSelect
              value={values.companyStateId}
              onChange={(event) => update("companyStateId", event.target.value)}
            >
              <option value="">Select state</option>
              {stateData
                .filter((state) => {
                  const countryID =
                    state.countryId?._id || state.countryId || state.countryID;
                  return (
                    !values.companyCountryId ||
                    !countryID ||
                    String(countryID) === String(values.companyCountryId)
                  );
                })
                .map((state) => (
                  <option key={state._id} value={state._id}>
                    {state.stateName || state.name}
                  </option>
                ))}
            </ThemedSelect>
            {errors.companyStateId && <small>{errors.companyStateId}</small>}
          </label>
          <label>
            Postcode <b>*</b>
            <input
              inputMode="numeric"
              value={values.companyZipCode}
              onChange={(event) =>
                update("companyZipCode", event.target.value.replace(/\D/g, "").slice(0, 4))
              }
            />
            {errors.companyZipCode && <small>{errors.companyZipCode}</small>}
          </label>
        </div>
        <footer>
          <button type="button" className="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="primary" onClick={save} disabled={busy}>
            {busy ? "Creating…" : "Create organisation"}
          </button>
        </footer>
      </section>
    </div>
  );
}

OrganisationCreateDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
};
